/** @jsxImportSource @emotion/react */
import { AIPackageCard } from '@landtrustinc/design-system';
import React from 'react';

import type { ResolvedPackageCardData } from '../../../types';
import { packageCardLinkStyles } from '../styles';

type PackageCardProps = {
  packageCardData: ResolvedPackageCardData;
  isLoading: boolean;
  onCardClick?: (packageId: string, url: string) => void;
};

/**
 * Individual package card rendered from pre-fetched API data.
 */
const PackageCardComponent: React.FC<PackageCardProps> = ({
  packageCardData,
  isLoading,
  onCardClick,
}) => {
  const featureChecks = packageCardData.why?.criteria?.map(({ shortLabel: label, matched }) => ({
    label,
    matched,
  }));
  const reviewEvidence = packageCardData.supportingEvidence?.find(
    ({ entityType, entityId }) => entityType === 'review' && !!entityId
  );

  const packageCardUrl =
    packageCardData.url && reviewEvidence?.entityId
      ? `${packageCardData.url.split('#')[0]}#review-${reviewEvidence.entityId}`
      : packageCardData.url;

  const cardContent = (
    <AIPackageCard
      images={packageCardData.images ?? []}
      title={packageCardData.title ?? ''}
      subtitle={packageCardData.shortSubtitle ?? packageCardData.subtitle}
      startingPrice={packageCardData.startingPrice}
      rating={packageCardData.rating}
      reviewCount={packageCardData.reviewCount}
      featureChecks={featureChecks}
      description={packageCardData.why?.narrative}
      ctaLabel={reviewEvidence ? 'Read Relevant Review' : undefined}
      isLoading={isLoading}
    />
  );

  // If we have data with a URL, wrap in a link
  if (packageCardUrl) {
    return (
      <a
        href={packageCardUrl}
        css={packageCardLinkStyles}
        onClick={() => onCardClick?.(packageCardData.packageId, packageCardUrl)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {cardContent}
      </a>
    );
  }

  return cardContent;
};

export default PackageCardComponent;
